import {
  HabitSchedule,
  ScheduleEntry,
  ScheduleEntryState,
  ScheduleFrequency,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { isBefore, isSameDay, max, startOfDay } from 'date-fns';

import { getStoredEndDate } from './get-display-end-date';
import {
  getFirstOccurrenceFrom,
  getScheduleTimeframeByDate,
} from './get-schedule-timeframe-by-date';
import { hasScheduleDefinitionChanged } from './has-schedule-definition-changed';

export type ScheduleEditDraft = {
  startDate: Date;
  endDate: Date | null;
  completionTargetCount: number;
  startTime: number | null;
  duration: number | null;
  rrule: HabitSchedule['rrule'];
  frequency: ScheduleFrequency | null;
};

export type BuildUpdatedScheduleOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
};

// An x-per-period habit plans days inside its period, so its start never moves them.
const hasStartDateMovedDays = (
  initial: HabitSchedule,
  updated: HabitSchedule,
) => {
  if (updated.frequency || isSameDay(initial.startDate, updated.startDate)) {
    return false;
  }

  const from = startOfDay(max([initial.startDate, updated.startDate]));
  const before = getFirstOccurrenceFrom(initial, from);
  const after = getFirstOccurrenceFrom(updated, from);

  return before && after ? !isSameDay(before, after) : before !== after;
};

// Same pattern on new days: the rrule re-supplies them, so prune in place and keep the history rather than split the schedule.
const dropMovedPlanning = (
  schedule: HabitSchedule,
  { today, weekStartsOn }: BuildUpdatedScheduleOptions,
): HabitSchedule => {
  const startTimeframe = getScheduleTimeframeByDate(
    schedule,
    schedule.startDate,
    weekStartsOn,
  );
  const plannedCutoff = new Date(
    Math.min(
      startTimeframe.startDate.getTime(),
      startOfDay(schedule.startDate).getTime(),
    ),
  );
  const skipCutoff = startOfDay(max([schedule.startDate, today]));

  const isDroppedSkip = ({ date, hidden, state }: ScheduleEntry) =>
    !!hidden &&
    state === ScheduleEntryState.Skip &&
    !isBefore(date, skipCutoff);

  const droppedSkipDates = schedule.entries
    .filter(isDroppedSkip)
    .map(({ date }) => date);
  const entries = schedule.entries.filter((entry) => !isDroppedSkip(entry));
  const keptPlannedOccurrences =
    schedule.plannedOccurrences?.filter(({ date }) =>
      isBefore(date, plannedCutoff),
    ) ?? [];

  const isKeptLegacySkip = (skip: Timestamp) =>
    !droppedSkipDates.some((date) => isSameDay(date, skip)) ||
    entries.some(
      ({ date, state }) =>
        state === ScheduleEntryState.Skip && isSameDay(date, skip),
    );

  return {
    ...schedule,
    entries,
    plannedOccurrences: keptPlannedOccurrences.length
      ? keptPlannedOccurrences
      : null,
    ...(schedule.skips
      ? { skips: schedule.skips.filter(isKeptLegacySkip) }
      : {}),
  };
};

// rrule/frequency come from the draft, not the live schedule, so the save-time prompt sees a recurrence change.
export const buildUpdatedSchedule = (
  schedule: HabitSchedule,
  draft: ScheduleEditDraft,
  options: BuildUpdatedScheduleOptions,
): HabitSchedule => {
  const timeslot = {
    ...schedule.timeslot,
    startTime: draft.startTime,
    duration: draft.duration,
  };

  const updated: HabitSchedule = {
    ...schedule,
    rrule: draft.rrule,
    frequency: draft.frequency,
    startDate: draft.startDate,
    endDate: getStoredEndDate(draft.endDate),
    completionTarget: {
      ...schedule.completionTarget,
      count: draft.completionTargetCount,
    },
    timeslot,
  };

  // Stored occurrences describe the old pattern's days; the rrule re-supplies the new ones.
  if (hasScheduleDefinitionChanged(schedule, updated)) {
    return { ...updated, plannedOccurrences: null };
  }

  const replanned = hasStartDateMovedDays(schedule, updated)
    ? dropMovedPlanning(updated, options)
    : updated;

  const timeslotChanged =
    (schedule.timeslot?.startTime ?? null) !== timeslot.startTime ||
    (schedule.timeslot?.duration ?? null) !== timeslot.duration;

  if (!timeslotChanged || !replanned.plannedOccurrences?.length) {
    return replanned;
  }

  // A stored occurrence outranks schedule.timeslot on every read.
  return {
    ...replanned,
    plannedOccurrences: replanned.plannedOccurrences.map((occurrence) => ({
      ...occurrence,
      timeslot,
    })),
  };
};
